import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { milestones } from "@/data/site";
import { SectionTag } from "@/components/SectionTag";

export const AttentionTimeline = () => {
  const ref = useRef(null);
  const [seconds, setSeconds] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const fill = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useMotionValueEvent(scrollYProgress, "change", (v) => {
    setSeconds(Math.min(60, Math.max(0, v * 60)));
  });

  let activeIndex = 0;
  milestones.forEach((m, i) => {
    if (seconds >= m.t - 0.05) activeIndex = i;
  });
  const active = milestones[activeIndex];

  return (
    <section ref={ref} data-testid="attention-timeline-section" className="relative h-[300vh] border-t border-white/5 bg-[#08090B]">
      <div className="sticky top-0 flex h-screen flex-col justify-center overflow-hidden">
        <div className="mx-auto w-full max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionTag testId="attention-timeline-tag">Chapter 04 — Time</SectionTag>
          <h2 className="mt-6 max-w-2xl font-display text-2xl font-semibold tracking-tight text-[#F4F4F6] sm:text-3xl lg:text-4xl">
            Every second of attention matters.
          </h2>
          <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-[#9498A4]">
            Kronos designs digital experiences around the limited time businesses have to
            earn interest, communicate value, and inspire action.
          </p>

          <div className="mt-14 grid items-end gap-12 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <div className="flex items-baseline gap-3">
                <span data-testid="attention-timer" className="font-mono text-6xl font-light tabular-nums text-[#F4F4F6] sm:text-7xl lg:text-8xl">
                  {seconds.toFixed(1)}
                </span>
                <span className="font-mono text-sm uppercase tracking-[0.3em] text-[#5A5E6B]">seconds</span>
              </div>

              <div className="relative mt-10">
                <div className="h-px w-full bg-white/10" />
                <motion.div
                  data-testid="attention-progress-fill"
                  className="absolute left-0 top-0 h-px bg-[#2563EB] shadow-[0_0_16px_rgba(37,99,235,0.9)]"
                  style={{ width: fill }}
                />
                <div className="absolute inset-x-0 top-0 flex justify-between">
                  {milestones.map((m, i) => (
                    <div key={m.t} className="relative -top-[3px] flex flex-col items-center">
                      <span
                        className={`h-[7px] w-[7px] rounded-full transition-all duration-500 ${
                          seconds >= m.t - 0.05 ? "bg-[#2563EB] shadow-[0_0_10px_rgba(37,99,235,0.8)]" : "bg-white/20"
                        }`}
                      />
                      <span className={`mt-3 font-mono text-[10px] tracking-widest transition-colors duration-500 ${i <= activeIndex ? "text-[#9498A4]" : "text-[#3A3E48]"}`}>
                        {m.t.toFixed(1)}s
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-12 h-24">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeIndex}
                    data-testid="attention-active-milestone"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -14 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <p className="font-display text-xl font-semibold text-[#F4F4F6] sm:text-2xl">{active.label}</p>
                    <p className="mt-2 text-sm text-[#9498A4]">{active.description}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            <div className="hidden flex-col gap-1 lg:flex">
              {milestones.map((m, i) => (
                <div
                  key={m.t}
                  data-testid={`attention-milestone-${i}`}
                  className={`flex items-baseline gap-5 border-l py-4 pl-6 transition-all duration-500 ${
                    i === activeIndex ? "border-[#2563EB] opacity-100" : "border-white/10 opacity-35"
                  }`}
                >
                  <span className="font-mono text-xs tabular-nums text-[#5A5E6B]">{m.t.toFixed(1)}s</span>
                  <div>
                    <p className="font-display text-base font-semibold text-[#F4F4F6]">{m.label}</p>
                    <p className="mt-1 text-xs leading-relaxed text-[#9498A4]">{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
